import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import colors from '@/assets/scss/_themes-vars.module.scss'

import AddBiologicalDialog, { AddBiologicalFormValues } from './AddBiologicalDialog'
import Biological, { BiologicalProps } from './Biological'

export type BiologicalsProps = {
  biologicals: {
    label: string
    comment: string
    unit: string
    value: string
    files: BiologicalProps['files']
    id: string
  }[]
  isCreatingBiological?: boolean
  isDeletingBiological?: boolean
  isDeletingBiologicalFiles?: boolean
  onCreateBiological?: (values: AddBiologicalFormValues) => Promise<void>
  onDeleteBiological?: (biologicalId: string) => void
  onDeleteBiologicalFiles?: BiologicalProps['onDeleteFiles']
  onUploadBiologicalFile: (file: File, biologicalId: string) => void
  onExtractVitalSigns?: (fileKey: string) => void
  isProcessingVitalSigns?: boolean
  processingVitalSignsFileKey?: string | null
  consultationId?: string
  readOnly?: boolean
}

function Biologicals(props: BiologicalsProps) {
  const {
    biologicals,
    isCreatingBiological = false,
    isDeletingBiologicalFiles = false,
    onCreateBiological,
    onDeleteBiological,
    onDeleteBiologicalFiles,
    onUploadBiologicalFile,
    onExtractVitalSigns,
    isProcessingVitalSigns = false,
    processingVitalSignsFileKey = null,
    consultationId,
    readOnly = false,
  } = props
  const { t } = useTranslation()

  const [openAddBiologicalDialog, setOpenAddBiologicalDialog] = useState(false)

  return (
    <Container>
      <Stack sx={{ height: '100%' }} direction="column" spacing={'20px'} px="10px" py="10px" justifyContent="space-between" alignItems="stretch">
        <Stack sx={{ position: 'sticky' }} direction="row" justifyContent="space-between" alignItems="stretch" px="10" py="10">
          <Typography color={colors.primaryMain} fontWeight={800} fontSize={20}>
            {t('component.group.biologicals.title')}
          </Typography>
          {!readOnly && (
            <Button variant="contained" type="button" onClick={() => setOpenAddBiologicalDialog(true)}>
              {t('component.group.biologicals.addBiologicalButton.label')}
            </Button>
          )}
        </Stack>
        <Stack direction="column">
          <Grid container spacing={2}>
            {biologicals.map((biological, index) => {
              return (
                <Grid key={`${biological.label} - ${index}`} item xs={12}>
                  <Biological
                    comment={biological.comment}
                    files={biological.files}
                    id={biological.id}
                    isDeletingFiles={isDeletingBiologicalFiles}
                    label={biological.label}
                    onDeleteBiological={onDeleteBiological}
                    onDeleteFiles={onDeleteBiologicalFiles}
                    onUploadFile={onUploadBiologicalFile}
                    onExtractVitalSigns={onExtractVitalSigns}
                    isProcessingVitalSigns={isProcessingVitalSigns}
                    processingVitalSignsFileKey={processingVitalSignsFileKey}
                    consultationId={consultationId}
                    readOnly={readOnly}
                    unit={biological.unit}
                    value={biological.value}
                  />
                </Grid>
              )
            })}
          </Grid>
        </Stack>
      </Stack>
      <AddBiologicalDialog
        open={openAddBiologicalDialog}
        onSubmit={async (values) => {
          await onCreateBiological?.(values)
          setOpenAddBiologicalDialog(false)
        }}
        onClose={() => setOpenAddBiologicalDialog(false)}
        loading={isCreatingBiological}
      />
    </Container>
  )
}

export default Biologicals
